import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, FileText, Loader2, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXT = [".pdf", ".doc", ".docx"];
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  current_role: "",
  current_company: "",
  message: "",
};

function useQueryParam(name) {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name) || "";
}

export default function Apply() {
  const nav = useNavigate();
  const role = useQueryParam("role");
  const jobId = useQueryParam("job");
  const [form, setForm] = useState(initial);
  const [cv, setCv] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!role) {
      // Allow open applications too, but give a clear label
    }
  }, [role]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validateCv = (file) => {
    if (!file) return "Please attach your CV.";
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext) && !ALLOWED_TYPES.includes(file.type)) {
      return "Only PDF, DOC or DOCX files are allowed.";
    }
    if (file.size > MAX_BYTES) return "CV must be 5 MB or smaller.";
    if (file.size === 0) return "File is empty.";
    return null;
  };

  const onFile = (file) => {
    const err = validateCv(file);
    if (err) {
      setErrors((e) => ({ ...e, cv: err }));
      setCv(null);
      return;
    }
    setErrors((e) => ({ ...e, cv: undefined }));
    setCv(file);
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    const cvErr = validateCv(cv);
    if (cvErr) e.cv = cvErr;
    if (!role && (!form.message || form.message.trim().length < 5))
      e.message = "Tell us a bit about the role you're looking for.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("role_applied", role || "Open application");
      if (jobId) fd.append("job_id", jobId);
      if (form.phone) fd.append("phone", form.phone);
      if (form.current_role) fd.append("current_role", form.current_role);
      if (form.current_company) fd.append("current_company", form.current_company);
      if (form.message) fd.append("message", form.message);
      fd.append("cv", cv);
      await axios.post(`${API}/applications`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Application received — we'll be in touch.");
      setDone(true);
      setForm(initial);
      setCv(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="apply-page" className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <div className="w-16 h-16 mx-auto bg-forest/10 flex items-center justify-center mb-8">
          <FileText className="w-7 h-7 text-forest" strokeWidth={1.5} />
        </div>
        <div className="overline text-terracotta mb-4">Application received</div>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-ink">
          Thank you.
        </h1>
        <p className="mt-6 text-muted2 leading-relaxed">
          Our team reviews every CV within one business day. If your profile fits, a Novacore
          consultant will reach out directly at <span className="text-forest">{form.email || "your email"}</span>.
        </p>
        <div className="mt-10 flex justify-center gap-3 flex-wrap">
          <Link to="/careers" className="btn-ghost" data-testid="apply-back-careers">
            <ArrowLeft className="w-4 h-4" /> Back to careers
          </Link>
          <button
            data-testid="apply-another"
            onClick={() => setDone(false)}
            className="btn-primary"
          >
            Apply for another role <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page">
      <section className="py-20 md:py-28 border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <Link
              to="/careers"
              data-testid="apply-back"
              className="inline-flex items-center gap-2 text-sm text-muted2 hover:text-forest mb-6"
            >
              <ArrowLeft className="w-4 h-4" /> Careers
            </Link>
            <div className="overline text-terracotta mb-3">
              {role ? "Apply for" : "Open application"}
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02] text-ink">
              {role || "Send us your CV"}
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-muted2 leading-relaxed">
              PDF, DOC or DOCX up to 5 MB. We&rsquo;ll review your CV and get back within
              one business day.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <form
          onSubmit={submit}
          data-testid="apply-form"
          noValidate
          className="max-w-3xl mx-auto px-6 md:px-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Full name*</Label>
              <Input
                data-testid="apply-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder=""
                className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
              />
              {errors.name && <p data-testid="err-name" className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Email*</Label>
              <Input
                type="email"
                data-testid="apply-email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder=""
                className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
              />
              {errors.email && <p data-testid="err-email" className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Phone</Label>
              <Input
                data-testid="apply-phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder=""
                className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Current role</Label>
              <Input
                data-testid="apply-current-role"
                value={form.current_role}
                onChange={(e) => set("current_role", e.target.value)}
                placeholder=""
                className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Current company</Label>
              <Input
                data-testid="apply-current-company"
                value={form.current_company}
                onChange={(e) => set("current_company", e.target.value)}
                placeholder="Where you work today"
                className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">
                {role ? "Cover message (optional)" : "What kind of role are you looking for?*"}
              </Label>
              <Textarea
                data-testid="apply-message"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder={role ? "Anything you'd like the hiring manager to know..." : "Tell us the role, team, or specialisation you're interested in."}
                rows={5}
                className="mt-2 border-hairline focus-visible:ring-2 focus-visible:ring-forest resize-none"
              />
              {errors.message && <p data-testid="err-message" className="mt-1 text-xs text-red-600">{errors.message}</p>}
            </div>

            {/* CV upload */}
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">CV*</Label>
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => onFile(e.target.files?.[0])}
                className="hidden"
                data-testid="apply-cv-input"
              />
              {!cv ? (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  data-testid="apply-cv-choose"
                  className="mt-2 w-full border-2 border-dashed border-hairline hover:border-forest hover:bg-forest/5 transition-colors p-8 text-center flex flex-col items-center gap-3"
                >
                  <Upload className="w-6 h-6 text-forest" strokeWidth={1.5} />
                  <div className="font-medium text-ink">Click to upload your CV</div>
                  <div className="text-xs text-muted2">PDF, DOC or DOCX · up to 5 MB</div>
                </button>
              ) : (
                <div
                  data-testid="apply-cv-selected"
                  className="mt-2 flex items-center justify-between gap-3 border border-hairline bg-forest/5 p-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-forest shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{cv.name}</div>
                      <div className="text-xs text-muted2">{Math.round(cv.size / 1024)} KB</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCv(null)}
                    data-testid="apply-cv-remove"
                    className="text-muted2 hover:text-red-600 p-1"
                    aria-label="Remove CV"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {errors.cv && <p data-testid="err-cv" className="mt-1 text-xs text-red-600">{errors.cv}</p>}
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-muted2">
              By applying, you consent to Novacore reaching out about relevant roles.
            </p>
            <button
              type="submit"
              data-testid="apply-submit"
              disabled={busy}
              className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>Sending <Loader2 className="w-4 h-4 animate-spin" /></>
              ) : (
                <>Submit application <ArrowUpRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
